import { useMemo } from 'react'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { MALDIVES_BUNDLE_DEFAULT_NIGHTS, MALDIVES_CHARTER_START_TARGET_DATE } from 'constants/charterFlight'

interface UseNextValidCharterPeriodParams {
  checkIn?: string
  checkOut?: string
}

interface NextValidCharterPeriod {
  fallbackCheckinDate: string | undefined
  maldivesNights: number
  effectiveReturnDate: string | undefined
}

/**
 * Hook to calculate next valid charter period dates for Maldives bundle
 * - fallbackCheckinDate: Next available Tuesday (charter flight day) from user's checkIn or today
 * - maldivesNights: 5 or 12 nights based on user's selected duration (> 8.5 days → 12 nights)
 * - effectiveReturnDate: fallbackCheckinDate + maldivesNights
 */
export function useNextValidCharterPeriod(params: UseNextValidCharterPeriodParams): NextValidCharterPeriod {
  const { checkIn, checkOut } = params

  const fallbackCheckinDate = useMemo(() => {
    const startDate = moment(MALDIVES_CHARTER_START_TARGET_DATE).startOf('day')

    if (checkIn) {
      const checkInMoment = moment(checkIn).startOf('day')

      // If checkIn is Tuesday, use it
      if (checkInMoment.isoWeekday() === 2) {
        // Ensure it's not before start date
        if (checkInMoment.isBefore(startDate)) {
          // If start date is Tuesday, use it; otherwise find the next Tuesday after start date
          if (startDate.isoWeekday() === 2) {
            return startDate.format(ISO_DATE_FORMAT)
          }
          return startDate.isoWeekday(9).format(ISO_DATE_FORMAT)
        }
        return checkInMoment.format(ISO_DATE_FORMAT)
      }

      // Find the next Tuesday: try this week's Tuesday first, then next week's if it's already passed
      const thisWeekTuesday = checkInMoment.clone().isoWeekday(2)
      const tuesdayAfterCheckIn = thisWeekTuesday.isSameOrAfter(checkInMoment, 'day')
        ? thisWeekTuesday
        : checkInMoment.isoWeekday(9)

      // Ensure the Tuesday is not before start date
      if (tuesdayAfterCheckIn.isBefore(startDate)) {
        // If start date is Tuesday, use it; otherwise find the next Tuesday after start date
        if (startDate.isoWeekday() === 2) {
          return startDate.format(ISO_DATE_FORMAT)
        }
        return startDate.isoWeekday(9).format(ISO_DATE_FORMAT)
      }
      return tuesdayAfterCheckIn.format(ISO_DATE_FORMAT)
    }

    // Calculate next Tuesday from today
    const today = moment().startOf('day')
    const thisWeekTuesday = today.clone().isoWeekday(2)
    const nextTuesday = thisWeekTuesday.isSameOrAfter(today, 'day')
      ? thisWeekTuesday
      : today.isoWeekday(9)
    const nextTuesdayMoment = moment(nextTuesday).startOf('day')

    // If next Tuesday is before start date, use the first Tuesday on or after start date
    if (nextTuesdayMoment.isBefore(startDate)) {
      // If start date is Tuesday, use it; otherwise find the next Tuesday after start date
      if (startDate.isoWeekday() === 2) {
        return startDate.format(ISO_DATE_FORMAT)
      }
      return startDate.isoWeekday(9).format(ISO_DATE_FORMAT)
    }
    return nextTuesday.format(ISO_DATE_FORMAT)
  }, [checkIn])

  // Calculate nights based on user selection or default
  const maldivesNights = useMemo(() => {
    // Default: 5 nights
    let nights = MALDIVES_BUNDLE_DEFAULT_NIGHTS

    // If user has selected both checkIn and checkOut, check the duration
    if (checkIn && checkOut) {
      const checkInMoment = moment(checkIn).startOf('day')
      const checkOutMoment = moment(checkOut).startOf('day')
      const selectedNights = checkOutMoment.diff(checkInMoment, 'days')

      // If selected duration is more than 8.5 days, use 12 nights
      if (selectedNights > 8.5) {
        nights = 12
      }
    }

    return nights
  }, [checkIn, checkOut])

  const effectiveReturnDate = useMemo(() => {
    if (!fallbackCheckinDate) {
      return undefined
    }

    return moment(fallbackCheckinDate).add(maldivesNights, 'days').format(ISO_DATE_FORMAT)
  }, [fallbackCheckinDate, maldivesNights])

  return {
    fallbackCheckinDate,
    maldivesNights,
    effectiveReturnDate,
  }
}
